/**
 * Calendar utility functions.
 */

export type EventColor =
  | 'blue'
  | 'green'
  | 'purple'
  | 'orange'
  | 'gray'
  | 'yellow';

export interface PastEventStyles {
  /** Tailwind classes for background */
  bgClasses: string;
  /** Tailwind classes for border */
  borderClasses: string;
  /** Tailwind classes for primary text */
  textClasses: string;
  /** Tailwind classes for secondary text */
  secondaryTextClasses: string;
  /** Inline style for border-left (for MonthEventChip and WeekDayColumn) */
  inlineBorderLeft?: string;
  /** Inline style for background-color (for WeekDayColumn) */
  inlineBackgroundColor?: string;
  /** Additional inline styles (color-mix fallback) */
  inlineStyles?: React.CSSProperties;
}

/**
 * Get past event styling for a given color.
 * Returns lighter/faded styles when isPast is true.
 *
 * Provides both:
 * - Tailwind classes for EventBlock (lighter shade classes)
 * - Inline styles for MonthEventChip and WeekDayColumn (color-mix with hex alpha fallback)
 *
 * @param color - The base color of the event
 * @param isPast - Whether the event is in the past
 * @param hexColor - Optional hex color for inline styles (MonthEventChip and WeekDayColumn)
 * @returns Object containing Tailwind classes and inline styles
 */
export function getPastEventStyles(
  color: EventColor,
  isPast: boolean,
  hexColor?: string
): PastEventStyles {
  if (!isPast) {
    // Present/future event: normal styles
    const normalStyles: Record<
      EventColor,
      Omit<
        PastEventStyles,
        'inlineBorderLeft' | 'inlineBackgroundColor' | 'inlineStyles'
      >
    > = {
      blue: {
        bgClasses: 'bg-blue-100 dark:bg-blue-900/30',
        borderClasses: 'border-l-blue-500',
        textClasses: 'text-blue-900 dark:text-blue-100',
        secondaryTextClasses: 'text-blue-700 dark:text-blue-300',
      },
      green: {
        bgClasses: 'bg-green-100 dark:bg-green-900/30',
        borderClasses: 'border-l-green-500',
        textClasses: 'text-green-900 dark:text-green-100',
        secondaryTextClasses: 'text-green-700 dark:text-green-300',
      },
      purple: {
        bgClasses: 'bg-purple-100 dark:bg-purple-900/30',
        borderClasses: 'border-l-purple-500',
        textClasses: 'text-purple-900 dark:text-purple-100',
        secondaryTextClasses: 'text-purple-700 dark:text-purple-300',
      },
      orange: {
        bgClasses: 'bg-orange-100 dark:bg-orange-900/30',
        borderClasses: 'border-l-orange-500',
        textClasses: 'text-orange-900 dark:text-orange-100',
        secondaryTextClasses: 'text-orange-700 dark:text-orange-300',
      },
      gray: {
        bgClasses: 'bg-gray-100 dark:bg-gray-700',
        borderClasses: 'border-l-gray-400',
        textClasses: 'text-primary',
        secondaryTextClasses: 'text-secondary',
      },
      yellow: {
        bgClasses: 'bg-[#FEFBE8] dark:bg-[#423307]',
        borderClasses: 'border-[#FEEE95] dark:border-[#A68B1D]',
        textClasses: 'text-[#A15C07] dark:text-[#FEEE95]',
        secondaryTextClasses: 'text-[#CA8504] dark:text-[#E0C06A]',
      },
    };

    const baseStyles = normalStyles[color];

    // Add inline styles for MonthEventChip and WeekDayColumn if hexColor provided
    if (hexColor) {
      const supportsColorMix =
        typeof CSS !== 'undefined' &&
        CSS.supports?.(
          'background-color',
          'color-mix(in srgb, red 15%, white)'
        );

      return {
        ...baseStyles,
        // Fallback values (used when color-mix not supported)
        inlineBorderLeft: `3px solid ${hexColor}`,
        inlineBackgroundColor: `${hexColor}26`, // 15% opacity
        // Modern browsers with color-mix: override with color-mix for background only
        // (border stays plain color for present events)
        inlineStyles: supportsColorMix
          ? {
              backgroundColor: `color-mix(in srgb, ${hexColor} 15%, var(--color-bg-primary))`,
            }
          : {},
      };
    }

    return baseStyles;
  }

  // Past event: lighter/faded styles
  // WCAG AA Compliance: Using -600 shades for 4.5:1 contrast ratio on light backgrounds
  const pastStyles: Record<
    EventColor,
    Omit<
      PastEventStyles,
      'inlineBorderLeft' | 'inlineBackgroundColor' | 'inlineStyles'
    >
  > = {
    blue: {
      bgClasses: 'bg-blue-50 dark:bg-blue-900/15',
      borderClasses: 'border-l-blue-300',
      textClasses: 'text-blue-600 dark:text-blue-300',
      secondaryTextClasses: 'text-blue-300 dark:text-blue-400',
    },
    green: {
      bgClasses: 'bg-green-50 dark:bg-green-900/15',
      borderClasses: 'border-l-green-300',
      textClasses: 'text-green-600 dark:text-green-300',
      secondaryTextClasses: 'text-green-300 dark:text-green-400',
    },
    purple: {
      bgClasses: 'bg-purple-50 dark:bg-purple-900/15',
      borderClasses: 'border-l-purple-300',
      textClasses: 'text-purple-600 dark:text-purple-300',
      secondaryTextClasses: 'text-purple-300 dark:text-purple-400',
    },
    orange: {
      bgClasses: 'bg-orange-50 dark:bg-orange-900/15',
      borderClasses: 'border-l-orange-300',
      textClasses: 'text-orange-600 dark:text-orange-300',
      secondaryTextClasses: 'text-orange-300 dark:text-orange-400',
    },
    gray: {
      bgClasses: 'bg-gray-50 dark:bg-gray-800',
      borderClasses: 'border-l-gray-300',
      textClasses: 'text-tertiary',
      secondaryTextClasses: 'text-quaternary',
    },
    yellow: {
      bgClasses: 'bg-[#FEFDF0] dark:bg-[#2d2205]',
      borderClasses: 'border-[#FEF5C3] dark:border-[#7A6520]',
      textClasses: 'text-[#D4A84A] dark:text-[#FEEE95]',
      secondaryTextClasses: 'text-[#E0C06A] dark:text-[#D4A84A]',
    },
  };

  const baseStyles = pastStyles[color];

  // Add inline styles for MonthEventChip and WeekDayColumn if hexColor provided
  if (hexColor) {
    const supportsColorMix =
      typeof CSS !== 'undefined' &&
      CSS.supports?.('background-color', 'color-mix(in srgb, red 15%, white)');

    return {
      ...baseStyles,
      // Fallback values (used when color-mix not supported)
      inlineBorderLeft: `3px solid ${hexColor}66`, // 40% opacity
      inlineBackgroundColor: `${hexColor}13`, // 8% opacity
      // Modern browsers with color-mix: override both border and background
      inlineStyles: supportsColorMix
        ? {
            borderLeft: `3px solid color-mix(in srgb, ${hexColor} 40%, var(--color-bg-primary))`,
            backgroundColor: `color-mix(in srgb, ${hexColor} 8%, var(--color-bg-primary))`,
          }
        : {},
    };
  }

  return baseStyles;
}

/**
 * Determine if an event has already ended (is in the past).
 *
 * Compares the event's end_time against the current time.
 * Events that have ended are typically displayed with reduced opacity or faded styling.
 *
 * @param event - Object with end_time property (ISO 8601 timestamp)
 * @returns true if the event has already ended, false otherwise
 */
export function isEventPast(event: { end_time: string }): boolean {
  return new Date(event.end_time) < new Date();
}
